import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import type { ContractVariantSpec } from '../api/types';
import { Modal } from '../components/Modal';
import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import {
  checkContractGuardrails,
  COMMISSION_MODELS,
  COMMISSION_PRESETS,
  CONSUMER_OBJECT_TYPES,
  CONTRACT_TEMPLATES,
  defaultContractVariant,
  fallbackContractVariant,
  formatPercentDe,
  OBJECT_TYPES,
  parsePercentDe,
  TERM_PRESETS,
} from './contractRules';

interface VariantState extends ContractVariantSpec {
  enabled: boolean;
}

export function ContractConfiguratorModal({
  leadId,
  appointmentId,
  initialObjectType = 'Einfamilienhaus',
  initialPrice = '',
  onClose,
}: {
  leadId: string;
  appointmentId?: string;
  initialObjectType?: string;
  initialPrice?: string;
  onClose: () => void;
}) {
  const { project } = useProject();
  const papi = useProjectApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [objectType, setObjectType] = useState(initialObjectType);
  const [variants, setVariants] = useState<VariantState[]>(() => [
    { ...defaultContractVariant(initialObjectType, 'Variante A (Empfehlung)', initialPrice), enabled: true },
  ]);
  const [brokerNote, setBrokerNote] = useState('');
  const [ownerWishEur, setOwnerWishEur] = useState<number>(() => {
    const cleaned = (initialPrice || '').replace(/\./g, '').replace(/,.*/, '').replace(/\D/g, '');
    return Number.parseInt(cleaned, 10) || 200000;
  });
  const [areaSqm, setAreaSqm] = useState<number>(100);
  const [sqmValueEur, setSqmValueEur] = useState<number>(1400);
  const [negotiationBufferPct, setNegotiationBufferPct] = useState<number>(15);

  const realisticMarketValue = Math.round((areaSqm * sqmValueEur) / 1000) * 1000;
  const recommendedStartPrice = Math.round((realisticMarketValue * (1 + negotiationBufferPct / 100)) / 1000) * 1000;
  const fmtEur = (n: number) => n.toLocaleString('de-DE');
  const wishDiffPct = realisticMarketValue > 0 ? Math.round(((ownerWishEur - realisticMarketValue) / realisticMarketValue) * 100) : 0;

  const applyRecommendedPrices = (withWishVariant: boolean) => {
    setVariants((cur) => {
      const first = cur[0] ?? { ...defaultContractVariant(objectType, 'Variante A (Empfehlung)', fmtEur(recommendedStartPrice)), enabled: true };
      const updatedFirst: VariantState = {
        ...first,
        label: `Variante A (Empfohlener Startpreis: ${fmtEur(recommendedStartPrice)} €)`,
        price: fmtEur(recommendedStartPrice),
        enabled: true,
      };
      if (!withWishVariant) {
        return [updatedFirst, ...cur.slice(1)];
      }
      const second: VariantState = {
        ...(cur[1] ?? fallbackContractVariant(updatedFirst, 1)),
        label: `Variante B (Kundenwunsch: ${fmtEur(ownerWishEur)} €)`,
        price: fmtEur(ownerWishEur),
        enabled: true,
      };
      return [updatedFirst, second, ...cur.slice(2, 3)];
    });
  };

  const applyObjectType = (nextType: string) => {
    setObjectType(nextType);
    const isConsumer = CONSUMER_OBJECT_TYPES.has(nextType);
    setVariants((cur) =>
      cur.map((v, idx) =>
        idx === 0
          ? {
              ...v,
              object_type: nextType,
              commission_model: isConsumer ? 'Halbteilung' : 'Verkäufer mit Weitergabe an den Käufer',
              commission_seller: isConsumer ? '3,57' : '0',
              commission_buyer: isConsumer ? '3,57' : '7,14',
              legal_override: false,
            }
          : { ...v, object_type: nextType },
      ),
    );
  };

  const updateVariant = (index: number, patch: Partial<VariantState>) => {
    setVariants((cur) => cur.map((v, i) => (i === index ? { ...v, ...patch } : v)));
  };

  const addVariant = () => {
    if (variants.length >= 3) return;
    const base = variants[0] ?? defaultContractVariant(objectType, 'Variante A', initialPrice);
    setVariants([...variants, { ...fallbackContractVariant(base, variants.length), enabled: true }]);
  };

  const activeVariants = variants.filter((v) => v.enabled);
  const unacknowledgedWarnings = activeVariants.some((v) => checkContractGuardrails(v).length > 0 && !v.legal_override);

  const create = useMutation({
    mutationFn: async (opts: { handToSupport: boolean; submitApproval: boolean; printFirst: boolean }) => {
      const payloadVariants: ContractVariantSpec[] = activeVariants.map((v) => ({
        label: v.label,
        template_key: v.template_key,
        object_type: v.object_type,
        price: v.price,
        commission_model: v.commission_model,
        commission_seller: v.commission_seller,
        commission_buyer: v.commission_buyer,
        term_months: v.term_months,
        special_agreements: v.special_agreements,
        individual_agreement: v.individual_agreement,
        legal_override: v.legal_override,
        legal_override_reason: v.legal_override_reason,
      }));
      const res = await papi.createContractVariants(leadId, {
        appointment_id: appointmentId ?? null,
        variants: payloadVariants,
        hand_to_support: opts.handToSupport,
        submit_approval: opts.submitApproval,
        broker_note: brokerNote.trim() || undefined,
      });
      return { res, printFirst: opts.printFirst };
    },
    onSuccess: ({ res, printFirst }) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.leadDocuments(project.id, leadId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.contractCockpit(project.id) });
      onClose();
      const first = res.documents[0];
      if (first) {
        navigate(`/p/${project.key}/dokumente/${first.id}${printFirst ? '?drucken=1' : ''}`);
      }
    },
  });

  return (
    <Modal
      title="Auftrags-Konfigurator (Slider & Varianten)"
      size="wide"
      onClose={onClose}
      actions={
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn"
            disabled={activeVariants.length === 0 || unacknowledgedWarnings || create.isPending}
            onClick={() => create.mutate({ handToSupport: false, submitApproval: false, printFirst: true })}
          >
            Anlegen &amp; drucken ({activeVariants.length})
          </button>
          <button
            type="button"
            className="btn"
            disabled={activeVariants.length === 0 || unacknowledgedWarnings || create.isPending}
            onClick={() => create.mutate({ handToSupport: false, submitApproval: true, printFirst: false })}
          >
            Anlegen &amp; öffnen ({activeVariants.length})
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={activeVariants.length === 0 || unacknowledgedWarnings || create.isPending}
            onClick={() => create.mutate({ handToSupport: true, submitApproval: true, printFirst: false })}
          >
            An Support (Melisa) &amp; zur Freigabe →
          </button>
        </div>
      }
    >
      <p className="muted">
        Das System schlägt je Objektart die empfohlenen Standardwerte vor. Für Sonderwünsche vor Ort oder mehrere Optionen zum Ausdrucken einfach die Regler
        verschieben oder eine zweite/dritte Variante dazuschalten.
      </p>

      <div className="card" style={{ marginBottom: '12px' }}>
        <span className="eyebrow">1. Objektart wählen (setzt passende Standard-Empfehlung)</span>
        <div className="form-actions" style={{ marginTop: '8px', flexWrap: 'wrap' }}>
          {OBJECT_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              className={objectType === t ? 'btn btn-small btn-primary' : 'btn btn-small'}
              onClick={() => applyObjectType(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <details className="card" style={{ marginBottom: '12px' }} open>
        <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
          iPad-Marktwert- &amp; Verhandlungsspannen-Rechner (Kundenwunsch vs. Marktwert vor Ort)
        </summary>
        <div className="form-grid" style={{ marginTop: '10px' }}>
          <label className="field">
            <span>Kundenwunsch (€): <strong>{fmtEur(ownerWishEur)} €</strong></span>
            <input
              type="number"
              min={10000}
              step={5000}
              value={ownerWishEur}
              onChange={(e) => setOwnerWishEur(Math.max(0, Number.parseInt(e.target.value || '0', 10)))}
            />
          </label>
          <label className="field">
            <span>Wohn-/Nutzfläche: <strong>{areaSqm} m²</strong></span>
            <input
              type="range"
              min={25}
              max={2500}
              step={5}
              value={Math.min(2500, Math.max(25, areaSqm))}
              onChange={(e) => setAreaSqm(Number.parseInt(e.target.value, 10))}
            />
            <input
              type="number"
              min={10}
              step={5}
              value={areaSqm}
              onChange={(e) => setAreaSqm(Math.max(10, Number.parseInt(e.target.value || '0', 10)))}
            />
          </label>
          <label className="field">
            <span>Realistischer Richtwert: <strong>{fmtEur(sqmValueEur)} €/m²</strong></span>
            <input
              type="range"
              min={500}
              max={10000}
              step={50}
              value={Math.min(10000, Math.max(500, sqmValueEur))}
              onChange={(e) => setSqmValueEur(Number.parseInt(e.target.value, 10))}
            />
          </label>
          <label className="field">
            <span>Verhandlungsspielraum: <strong>+{negotiationBufferPct} %</strong></span>
            <input
              type="range"
              min={0}
              max={30}
              step={1}
              value={negotiationBufferPct}
              onChange={(e) => setNegotiationBufferPct(Number.parseInt(e.target.value, 10))}
            />
          </label>
        </div>
        <div
          className="alert alert-info"
          style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}
        >
          <div>
            <strong>Marktwert realistisch: {fmtEur(realisticMarketValue)} €</strong> ·{' '}
            <strong>Empfohlener Startpreis (+{negotiationBufferPct} % Spielraum): {fmtEur(recommendedStartPrice)} €</strong>
            <div className="small muted">
              Kundenwunsch ({fmtEur(ownerWishEur)} €) liegt {wishDiffPct >= 0 ? `+${wishDiffPct} % über` : `${wishDiffPct} % unter`} dem realistischen Marktwert.
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-small" onClick={() => applyRecommendedPrices(false)}>
              Startpreis ({fmtEur(recommendedStartPrice)} €) übernehmen
            </button>
            <button type="button" className="btn btn-small btn-primary" onClick={() => applyRecommendedPrices(true)}>
              Startpreis (A) + Kundenwunsch (B) anlegen
            </button>
          </div>
        </div>
      </details>

      {variants.map((v, idx) => {
        const warnings = checkContractGuardrails(v);
        const sellerNum = parsePercentDe(v.commission_seller);
        const buyerNum = parsePercentDe(v.commission_buyer);
        const termNum = Number.parseInt(v.term_months || '6', 10) || 6;
        return (
          <section key={idx} className="card" style={{ marginBottom: '12px', opacity: v.enabled ? 1 : 0.6 }}>
            <div className="section-head">
              <label className="checkbox" style={{ fontWeight: 700 }}>
                <input
                  type="checkbox"
                  checked={v.enabled}
                  onChange={(e) => updateVariant(idx, { enabled: e.target.checked })}
                />{' '}
                {v.label || `Variante ${String.fromCharCode(65 + idx)}`}
              </label>
              {idx > 0 && (
                <button
                  type="button"
                  className="btn btn-small btn-ghost"
                  onClick={() => setVariants((cur) => cur.filter((_, i) => i !== idx))}
                >
                  Variante entfernen
                </button>
              )}
            </div>

            <div className="form-grid" style={{ marginTop: '8px' }}>
              <label className="field">
                <span>Bezeichnung der Variante</span>
                <input
                  value={v.label ?? ''}
                  onChange={(e) => updateVariant(idx, { label: e.target.value })}
                  placeholder="z. B. Variante A (Standard)"
                />
              </label>

              <label className="field">
                <span>Vertragsart</span>
                <select
                  value={v.template_key}
                  onChange={(e) =>
                    updateVariant(idx, {
                      template_key: e.target.value as ContractVariantSpec['template_key'],
                      legal_override: false,
                    })
                  }
                >
                  {CONTRACT_TEMPLATES.map((tpl) => (
                    <option key={tpl.key} value={tpl.key}>
                      {tpl.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span>Provisionsmodell</span>
                <select
                  value={v.commission_model ?? 'Halbteilung'}
                  onChange={(e) => {
                    const model = e.target.value;
                    if (model === 'Nur Verkäufer (Innenprovision)') {
                      updateVariant(idx, { commission_model: model, commission_buyer: '0', legal_override: false });
                    } else if (model === 'Halbteilung') {
                      const eq = v.commission_seller && v.commission_seller !== '0' ? v.commission_seller : '3,57';
                      updateVariant(idx, { commission_model: model, commission_seller: eq, commission_buyer: eq, legal_override: false });
                    } else {
                      updateVariant(idx, { commission_model: model, legal_override: false });
                    }
                  }}
                >
                  {COMMISSION_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span>Angebotspreis (€ – leer = Linie zum Eintragen mit Stift)</span>
                <input
                  value={v.price ?? ''}
                  onChange={(e) => updateVariant(idx, { price: e.target.value })}
                  placeholder="z. B. 450.000 oder leer lassen"
                />
              </label>

              <div className="field wide">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    Provision Verkäufer (% inkl. USt.): <strong>{v.commission_seller || '0'} %</strong>
                  </span>
                  <div className="form-actions">
                    {COMMISSION_PRESETS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={v.commission_seller === p ? 'btn btn-small btn-primary' : 'btn btn-small'}
                        onClick={() =>
                          updateVariant(idx, {
                            commission_seller: p,
                            ...(v.commission_model === 'Halbteilung' ? { commission_buyer: p } : {}),
                            legal_override: false,
                          })
                        }
                      >
                        {p} %
                      </button>
                    ))}
                  </div>
                </div>
                <input
                  type="range"
                  aria-label={`Provision Verkäufer Variante ${idx + 1}`}
                  min={0}
                  max={7.14}
                  step={0.01}
                  value={sellerNum}
                  onChange={(e) => {
                    const formatted = formatPercentDe(Number.parseFloat(e.target.value));
                    updateVariant(idx, {
                      commission_seller: formatted,
                      ...(v.commission_model === 'Halbteilung' ? { commission_buyer: formatted } : {}),
                      legal_override: false,
                    });
                  }}
                />
              </div>

              {v.commission_model !== 'Nur Verkäufer (Innenprovision)' && (
                <div className="field wide">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      Provision Käufer (% inkl. USt.): <strong>{v.commission_buyer || '0'} %</strong>
                    </span>
                    <div className="form-actions">
                      {COMMISSION_PRESETS.map((p) => (
                        <button
                          key={p}
                          type="button"
                          className={v.commission_buyer === p ? 'btn btn-small btn-primary' : 'btn btn-small'}
                          onClick={() => updateVariant(idx, { commission_buyer: p, legal_override: false })}
                        >
                          {p} %
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="range"
                    aria-label={`Provision Käufer Variante ${idx + 1}`}
                    min={0}
                    max={7.14}
                    step={0.01}
                    value={buyerNum}
                    onChange={(e) => {
                      const formatted = formatPercentDe(Number.parseFloat(e.target.value));
                      updateVariant(idx, { commission_buyer: formatted, legal_override: false });
                    }}
                  />
                </div>
              )}

              {v.template_key !== 'maklervertrag_einfach' && (
                <div className="field wide">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                      Vertragslaufzeit: <strong>{v.term_months || '6'} Monate</strong>
                    </span>
                    <div className="form-actions">
                      {TERM_PRESETS.map((m) => (
                        <button
                          key={m}
                          type="button"
                          className={v.term_months === m ? 'btn btn-small btn-primary' : 'btn btn-small'}
                          onClick={() => updateVariant(idx, { term_months: m })}
                        >
                          {m} Mon.
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="range"
                    aria-label={`Laufzeit Variante ${idx + 1}`}
                    min={1}
                    max={24}
                    step={1}
                    value={termNum}
                    onChange={(e) => updateVariant(idx, { term_months: e.target.value })}
                  />
                </div>
              )}

              {v.template_key === 'maklervertrag_qualifiziert' && (
                <label className="field wide">
                  <span>Individuell ausgehandelte Verweisungs- und Eigengeschäftsklausel</span>
                  <textarea
                    rows={2}
                    value={v.individual_agreement ?? ''}
                    onChange={(e) => updateVariant(idx, { individual_agreement: e.target.value, legal_override: false })}
                    placeholder="Dokumentieren, was mit dem Eigentümer im Einzelnen ausgehandelt wurde …"
                  />
                </label>
              )}

              <label className="field wide">
                <span>Besondere Vereinbarungen (optional)</span>
                <input
                  value={v.special_agreements ?? ''}
                  onChange={(e) => updateVariant(idx, { special_agreements: e.target.value })}
                  placeholder="z. B. Vermarktungsstart ab …, diskrete Vermarktung ohne Portal …"
                />
              </label>
            </div>

            {warnings.length === 0 ? (
              <p className="badge badge-ok" style={{ marginTop: '8px' }}>
                Rechts-Schutzschild OK – gesetzliche Standardregeln (§ 656c/d BGB) eingehalten
              </p>
            ) : (
              <div className="alert alert-warning" style={{ marginTop: '10px' }}>
                <strong>Rechtlicher Hinweis (Schutzschild):</strong>
                <ul className="plain-list" style={{ margin: '6px 0' }}>
                  {warnings.map((w, wi) => (
                    <li key={wi}>{w}</li>
                  ))}
                </ul>
                <label className="checkbox" style={{ marginTop: '6px', fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(v.legal_override)}
                    onChange={(e) => updateVariant(idx, { legal_override: e.target.checked })}
                  />{' '}
                  Hinweis gelesen und bewusst ablehnen / übersteuern (Kunden- bzw. Maklerwunsch vor Ort geht vor)
                </label>
              </div>
            )}
          </section>
        );
      })}

      {variants.length < 3 && (
        <div style={{ marginBottom: '12px' }}>
          <button type="button" className="btn btn-small" onClick={addVariant}>
            + Weitere Variante hinzufügen (z. B. Ausweich-Option zum Mitdrucken)
          </button>
        </div>
      )}

      <label className="field wide">
        <span>Erfahrung / Anweisung für den Support (Melisa erhält daraus automatisch ihre Aufgabe auf Bosnisch)</span>
        <textarea
          rows={2}
          value={brokerNote}
          onChange={(e) => setBrokerNote(e.target.value)}
          placeholder="z. B. Eigentümer möchte erst Referenzen sehen / Vorvertrag prüfen / Unterschrift per E-Mail anfordern …"
        />
      </label>

      {create.isError && <MutationError error={create.error} />}
    </Modal>
  );
}
